import React, { useState, useEffect, useMemo } from 'react';
import { 
  Mail, RefreshCw, Star, Users, Ship, Trash2, Eye,
  TrendingUp, ArrowLeft, Calendar, Loader2, Wifi, MapPin, Search, ChevronRight, LayoutDashboard, User, MessageSquare, Inbox
} from 'lucide-react';
import { dbService } from '../services/dbService';
import { SiteContent, Review, Registration } from '../types';

interface AdminDashboardProps {
  onClose: () => void;
  currentContent: SiteContent;
}

const AdminDashboard: React.FC<AdminDashboardProps> = ({ onClose, currentContent }) => {
  const [activeTab, setActiveTab] = useState<'analytics' | 'manifest' | 'messages'>('analytics');
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [inquiries, setInquiries] = useState<any[]>([]);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dbStatus, setDbStatus] = useState<'checking' | 'connected' | 'offline'>('checking');
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [regs, inqs, revs, connected] = await Promise.all([
        dbService.fetchRegistrations(),
        dbService.fetchInquiries(),
        dbService.fetchReviews(),
        dbService.checkConnection()
      ]);
      setRegistrations(regs);
      setInquiries(inqs);
      setReviews(revs);
      setDbStatus(connected ? 'connected' : 'offline');
    } catch (err) {
      console.error("Dashboard Load Error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { 
    loadData(); 
  }, []);

  const handleDeleteRegistration = async (id: number) => {
    if (!confirm('Remove this entry from the guest registration records?')) return;
    setDeletingId(id);
    const success = await dbService.deleteRegistration(id);
    if (success) {
      setRegistrations(prev => prev.filter(r => r.id !== id));
    }
    setDeletingId(null);
  };

  const handleDeleteInquiry = async (id: number) => {
    if (!confirm('Are you sure you want to delete this visitor inquiry?')) return;
    setDeletingId(id);
    const success = await dbService.deleteInquiry(id);
    if (success) {
      setInquiries(prev => prev.filter(i => i.id !== id));
    }
    setDeletingId(null);
  };

  const stats = useMemo(() => ({
    voyages: registrations.length,
    explorers: registrations.reduce((s, r) => s + (r.guests || 1), 0),
    messages: inquiries.length,
    rating: reviews.length > 0 ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1) : '5.0'
  }), [registrations, inquiries, reviews]);

  const monthlyActivity = useMemo(() => {
    const counts = new Array(12).fill(0);
    registrations.forEach(reg => {
      const date = new Date(reg.created_at);
      if (!isNaN(date.getTime())) {
        counts[date.getMonth()]++;
      }
    });
    
    const max = Math.max(...counts, 1);
    return counts.map(count => ({
      count,
      height: count > 0 ? Math.max((count / max) * 100, 4) : 2 
    }));
  }, [registrations]);

  const StatCard = ({ title, value, icon: Icon }: any) => (
    <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex flex-col justify-between hover:border-slate-400 transition-all group">
      <div className="flex items-center justify-between mb-4">
        <span className="text-[10px] font-bold text-slate-700 uppercase tracking-wider">{title}</span>
        <div className="p-2 rounded-lg bg-slate-50 group-hover:bg-slate-950 group-hover:text-white transition-colors">
          <Icon className="w-4 h-4 text-slate-900 group-hover:text-white" />
        </div>
      </div>
      <div>
        <h4 className="text-4xl font-bold text-slate-950 tracking-tight">{value}</h4>
        <div className="mt-2 flex items-center gap-1.5">
          <TrendingUp className="w-3 h-3 text-slate-600" />
          <span className="text-[9px] text-slate-600 font-bold uppercase tracking-wider">Active Records</span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white flex flex-col md:flex-row text-slate-900 selection:bg-slate-200">
      <aside className="w-full md:w-64 bg-slate-950 border-r border-white/5 flex flex-col md:h-screen md:sticky md:top-0">
        <div className="p-8">
          <div className="flex items-center gap-3 cursor-pointer group" onClick={onClose}>
            <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-xl">
              <LayoutDashboard className="w-5 h-5 text-slate-950" />
            </div>
            <div className="flex flex-col">
              <span className="text-[12px] font-bold text-white tracking-widest uppercase">Tourism Office</span>
              <span className="text-[6px] font-bold text-white/40 tracking-wider uppercase">Admin Portal</span>
            </div>
          </div>
        </div>
        
        <nav className="flex-1 px-3 py-2 space-y-1">
          <button 
            onClick={() => setActiveTab('analytics')} 
            className={`w-full flex items-center gap-3.5 px-4 py-3 rounded-xl text-[10px] font-bold uppercase tracking-widest transition-all ${activeTab === 'analytics' ? 'bg-white/10 text-white' : 'text-white/50 hover:text-white hover:bg-white/5'}`}
          >
            <TrendingUp className="w-3.5 h-3.5" /> Dashboard
          </button>
          <button 
            onClick={() => setActiveTab('manifest')} 
            className={`w-full flex items-center gap-3.5 px-4 py-3 rounded-xl text-[10px] font-bold uppercase tracking-widest transition-all ${activeTab === 'manifest' ? 'bg-white/10 text-white' : 'text-white/50 hover:text-white hover:bg-white/5'}`}
          >
            <Users className="w-3.5 h-3.5" /> Guest Registrations
          </button>
          <button 
            onClick={() => setActiveTab('messages')} 
            className={`w-full flex items-center gap-3.5 px-4 py-3 rounded-xl text-[10px] font-bold uppercase tracking-widest transition-all ${activeTab === 'messages' ? 'bg-white/10 text-white' : 'text-white/50 hover:text-white hover:bg-white/5'}`}
          >
            <Mail className="w-3.5 h-3.5" /> Inquiries
          </button>
        </nav>
        
        <div className="p-6 mt-auto">
          <div className="bg-white/5 rounded-2xl p-5 mb-6 border border-white/10">
             <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                   <div className={`w-2 h-2 rounded-full ${dbStatus === 'connected' ? 'bg-emerald-500' : 'bg-amber-500'}`}></div>
                   <span className="text-[8px] font-bold text-white/80 uppercase tracking-widest">{dbStatus === 'connected' ? 'Cloud Sync' : 'Local Only'}</span>
                </div>
                <Wifi className={`w-3 h-3 ${dbStatus === 'connected' ? 'text-white' : 'text-white/40'}`} />
             </div>
             <p className="text-[9px] text-white/50 font-normal leading-relaxed">
               {dbStatus === 'connected' ? 'Live database is connected.' : 'Database offline. Data is currently stored in local browser cache.'}
             </p>
          </div>
          <button onClick={onClose} className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl bg-white text-slate-950 text-[10px] font-bold uppercase tracking-wider hover:bg-slate-200 transition-all">
            <ArrowLeft className="w-4 h-4" /> Return to Site
          </button>
        </div>
      </aside>

      <main className="flex-1 flex flex-col bg-white">
        <header className="h-20 bg-white border-b border-slate-200 px-10 flex items-center justify-between sticky top-0 z-20">
          <div className="flex items-center gap-3 text-[9px] text-slate-600 font-bold uppercase tracking-wider">
            <span className="hover:text-slate-950 transition-colors cursor-pointer" onClick={() => setActiveTab('analytics')}>Admin</span>
            <ChevronRight className="w-3 h-3 text-slate-400" />
            <span className="text-slate-950 font-extrabold capitalize">{activeTab}</span>
          </div>
          
          <div className="flex items-center gap-8">
            <button onClick={loadData} className="p-2 text-slate-600 hover:text-slate-950 transition-all flex items-center gap-2">
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              <span className="text-[8px] font-bold uppercase tracking-widest">Refresh</span>
            </button>
            <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center border border-slate-200">
              <User className="w-5 h-5 text-slate-950" />
            </div>
          </div>
        </header>

        <div className="p-10 max-w-[1600px] w-full mx-auto flex-1 flex flex-col">
          {isLoading ? (
            <div className="flex-1 flex flex-col items-center justify-center">
              <Loader2 className="w-10 h-10 text-slate-950 animate-spin mb-6" />
              <p className="text-[10px] font-bold text-slate-600 uppercase tracking-wider">Syncing Island Records...</p>
            </div>
          ) : activeTab === 'analytics' ? (
            <div className="space-y-10 animate-in fade-in duration-700">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                <StatCard title="Total Bookings" value={stats.voyages} icon={Ship} />
                <StatCard title="Guest Count" value={stats.explorers} icon={Users} />
                <StatCard title="Average Rating" value={stats.rating} icon={Star} />
                <StatCard title="Inquiries" value={stats.messages} icon={MessageSquare} />
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                <div className="lg:col-span-2 bg-white p-10 rounded-[2.5rem] border border-slate-200 shadow-sm">
                  <div className="flex items-center justify-between mb-12">
                    <h3 className="text-[11px] font-bold text-slate-950 uppercase tracking-wider">Visitor Trends</h3>
                    <div className="text-[10px] font-bold text-slate-600 uppercase tracking-wider">Current Year</div>
                  </div>
                  <div className="h-72 w-full flex items-end gap-3 px-2 pb-2">
                    {monthlyActivity.map((data, i) => (
                      <div 
                        key={i} 
                        className={`flex-1 transition-all duration-700 cursor-pointer group relative rounded-t-xl ${data.count > 0 ? 'bg-slate-950' : 'bg-slate-100'}`} 
                        style={{ height: `${data.height}%` }}
                      >
                        <div className="absolute -top-14 left-1/2 -translate-x-1/2 bg-slate-950 text-white text-[9px] font-bold uppercase py-2 px-4 rounded-xl shadow-2xl opacity-0 group-hover:opacity-100 transition-all z-10 whitespace-nowrap tracking-wider">
                          {data.count} Guests
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between mt-8 px-2 text-[10px] font-bold text-slate-600 uppercase tracking-wider">
                    {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map(m => <span key={m}>{m}</span>)}
                  </div>
                </div>
                
                <div className="bg-white p-10 rounded-[2.5rem] border border-slate-200 shadow-sm flex flex-col justify-center text-center">
                  <div className="p-6 bg-slate-50 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                    <MapPin className="w-8 h-8 text-slate-950" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-950 mb-2">Maritime Center</h3>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-6">Mabila, Sarangani</p>
                  <p className="text-slate-500 text-sm leading-relaxed px-4">The command center is actively processing travel authorizations for the southernmost frontier.</p>
                </div>
              </div>
            </div>
          ) : activeTab === 'manifest' ? (
            <div className="flex-1 flex flex-col">
              {registrations.length > 0 ? (
                <div className="bg-white rounded-[2.5rem] border border-slate-200 shadow-sm overflow-hidden animate-in slide-in-from-bottom-4 duration-700">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse min-w-[1000px]">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr className="text-[9px] font-bold text-slate-700 uppercase tracking-wider">
                          <th className="px-10 py-6">Guest Name</th>
                          <th className="px-10 py-6">Origin</th>
                          <th className="px-10 py-6">Travel Dates</th>
                          <th className="px-10 py-6 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {registrations.map(r => (
                          <tr key={r.id} className="hover:bg-slate-50 transition-colors group">
                            <td className="px-10 py-8">
                              <p className="font-bold text-slate-950 text-sm">{r.name}</p>
                              <p className="text-[10px] font-bold text-slate-600 uppercase tracking-wider mt-1">{r.email}</p>
                            </td>
                            <td className="px-10 py-8">
                              <div className="flex items-center gap-2 text-slate-800 mb-2">
                                <MapPin className="w-3.5 h-3.5 text-slate-600" />
                                <span className="text-[11px] font-medium">{r.city}, {r.country}</span>
                              </div>
                            </td>
                            <td className="px-10 py-8">
                              <div className="flex items-center gap-3 text-[11px] text-slate-800 font-medium">
                                <Calendar className="w-4 h-4 text-slate-600" />
                                {r.arrival_date} — {r.departure_date}
                              </div>
                            </td>
                            <td className="px-10 py-8 text-right">
                              <div className="flex items-center justify-end gap-3">
                                <button 
                                  onClick={() => handleDeleteRegistration(r.id)} 
                                  disabled={deletingId === r.id}
                                  className="p-2 text-slate-300 hover:text-red-500 transition-all"
                                >
                                  {deletingId === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center p-20 text-center border-4 border-dashed border-slate-100 rounded-[3rem] animate-in fade-in zoom-in-95">
                  <div className="w-24 h-24 bg-slate-50 rounded-full flex items-center justify-center mb-8">
                    <Ship className="w-10 h-10 text-slate-200" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-950 mb-3">No Registrations Yet</h3>
                  <p className="text-slate-400 text-sm max-w-xs mx-auto mb-10">When visitors fill out the "Book Your Visit" form, their records will appear in this manifest.</p>
                  <button onClick={loadData} className="px-8 py-3 bg-slate-950 text-white rounded-full text-[10px] font-bold uppercase tracking-widest hover:bg-sky-500 transition-all">Refresh Manifest</button>
                </div>
              )}
            </div>
          ) : activeTab === 'messages' ? (
            <div className="flex-1 flex flex-col">
              {inquiries.length > 0 ? (
                <div className="bg-white rounded-[2.5rem] border border-slate-200 shadow-sm overflow-hidden animate-in slide-in-from-bottom-4 duration-700">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse min-w-[1000px]">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr className="text-[9px] font-bold text-slate-700 uppercase tracking-wider">
                          <th className="px-10 py-6">Sender</th>
                          <th className="px-10 py-6">Subject</th>
                          <th className="px-10 py-6">Message Preview</th>
                          <th className="px-10 py-6 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {inquiries.map(iq => (
                          <tr key={iq.id} className="hover:bg-slate-50 transition-colors group cursor-pointer">
                            <td className="px-10 py-8">
                              <p className="font-bold text-slate-950 text-sm">{iq.name}</p>
                              <p className="text-[10px] font-bold text-slate-600 uppercase tracking-wider mt-1">{iq.email}</p>
                            </td>
                            <td className="px-10 py-8">
                               <span className="w-fit px-3 py-1 bg-slate-200 text-slate-950 rounded-full text-[8px] font-bold uppercase tracking-wider">{iq.subject}</span>
                            </td>
                            <td className="px-10 py-8">
                              <p className="text-[13px] text-slate-800 font-normal truncate max-w-sm">{iq.message}</p>
                              <p className="text-[9px] text-slate-400 mt-2 font-bold uppercase tracking-widest">{new Date(iq.created_at).toLocaleDateString()}</p>
                            </td>
                            <td className="px-10 py-8 text-right">
                              <div className="flex items-center justify-end gap-3">
                                <button 
                                  onClick={() => handleDeleteInquiry(iq.id)} 
                                  disabled={deletingId === iq.id}
                                  className="p-2 text-slate-300 hover:text-red-500 transition-all"
                                >
                                  {deletingId === iq.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center p-20 text-center border-4 border-dashed border-slate-100 rounded-[3rem] animate-in fade-in zoom-in-95">
                  <div className="w-24 h-24 bg-slate-50 rounded-full flex items-center justify-center mb-8">
                    <Inbox className="w-10 h-10 text-slate-200" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-950 mb-3">Empty Inbox</h3>
                  <p className="text-slate-400 text-sm max-w-xs mx-auto mb-10">Private visitor inquiries will appear here as soon as they are submitted from the Contact section.</p>
                  <button onClick={loadData} className="px-8 py-3 bg-slate-950 text-white rounded-full text-[10px] font-bold uppercase tracking-widest hover:bg-sky-500 transition-all">Check for Messages</button>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;